import { Check } from "lucide-react";

import {
  reviewStageIndex,
  reviewStageLabels,
} from "../utils/reviewStatus";

/**
 * A compact five-step progress strip for a 360 review:
 * Self → Colleagues → Manager → Meeting → Done.
 * Finished stages are solid, the current one is highlighted, the rest are
 * muted — so anyone can see where a review is without decoding statuses.
 */
export default function ReviewStagePips({
  status,
  size = "sm",
}: {
  status: string;
  size?: "sm" | "md";
}) {
  const current = reviewStageIndex(status);
  if (current < 0) return null;
  const labels = reviewStageLabels();
  const done = status === "completed";
  const pip = size === "md" ? "h-5 w-5 text-[10px]" : "h-4 w-4 text-[9px]";
  const text = size === "md" ? "text-[11px]" : "text-[10px]";

  return (
    <div className="flex items-center" aria-label={`Stage ${current + 1} of ${labels.length}`}>
      {labels.map((label, i) => {
        const isDone = i < current || done;
        const isCurrent = i === current && !done;
        return (
          <div key={label} className="flex items-center">
            {i > 0 && (
              <div
                className={`h-px w-3 sm:w-4 ${i <= current || done ? "bg-pme-blue" : "bg-gray-200"}`}
                aria-hidden
              />
            )}
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={[
                  "flex items-center justify-center rounded-full font-semibold",
                  pip,
                  isDone
                    ? "bg-pme-blue text-white"
                    : isCurrent
                      ? "bg-white text-pme-blue ring-2 ring-pme-blue"
                      : "bg-gray-100 text-gray-400",
                ].join(" ")}
              >
                {isDone ? <Check size={size === "md" ? 12 : 10} strokeWidth={2.5} aria-hidden /> : i + 1}
              </div>
              <span
                className={[
                  text,
                  "hidden sm:block leading-none",
                  isCurrent ? "font-semibold text-pme-blue" : isDone ? "text-gray-500" : "text-gray-400",
                ].join(" ")}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
